// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE PRODUCT — the concept, as screens.
//
// Erez, 15 Aug 2026: "The main screen is where you see the estimate. First the
// ballpark and then the detail, and you see it on the left. And on the right
// side you have big boxes that analyse each section of the estimate. You can
// click to open a right panel or a centre modal that gives you more detail...
// they explain to you why certain numbers were selected. Maybe they even give
// you a bar where you can change values and it affects the price. Put a bunch
// of options on the product section, and let's imagine the user experience."
//
// This REPLACES the eight captured screens that were here. Those were the August
// demo, and the concept page says in as many words that the demo does not
// represent the concept and must not be treated as a prototype of it. Keeping
// them beside the concept would have been the board saying two different things.
//
// WHAT IS HERE
//   0 · The journey — the concept's seven beats as the salesperson lives them.
//   A · The reasoning rail — every reasoning box open, permanently, beside the
//       section it explains. Nothing to click to see why a number is what it is.
//   B · The clean sheet + drawer — the estimate reads exactly as the PDF the
//       client receives; evidence is a margin anchor that opens a right panel.
//   C · The deep dive — a clean sheet where any NUMBER opens a centre modal
//       carrying the evidence and the levers.
//
// ALL THREE ARE ONE COMPONENT with a `variant` prop, deliberately. They are
// three treatments of ONE product, not three products, and building them from
// one source is what guarantees the comparison is honest: identical estimate,
// identical evidence, only the surfacing differs.
//
// THE ONE THING THAT IS NOT A MATTER OF TASTE. Option A puts the evidence
// adjacent to the value. That is the only intervention measured to reduce both
// missed errors and wrong acceptances — and we have our own numbers: Falcon 404
// of 420 agent-written values never confirmed, Outform 40 of 40. B and C are
// real options and they both re-introduce a navigation between the number and
// its reason. The note under the figures states that as a lean, not a verdict.
//
// INTERACTION LAW (inherited, and it is why nothing here writes): every
// affordance ASKS. The send-back button on a section composes a message to the
// agent; it does not edit the estimate. The margin slider is a what-if that
// moves the displayed price and stores nothing.
//
// ⚠️ EVERY NUMBER BELOW IS DEMONSTRATION DATA and the screens say so on their
// own face. Outform have sent us no job data. The shapes are theirs — their
// bands, their §7 three prices, their §10.1 reference tables, their §3.7.1
// score — the values are ours and are marked DEMO-.
//
// Owner agent: Quinn.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import { Canvas, box, leaf, person, type Diagram } from "../command/c4";
import { N, NFONT, NMONO, R, R_CTL } from "./notion";

const F = NFONT;
const MONO = NMONO;

// ═══ 0 · THE JOURNEY ═════════════════════════════════════════════════════════
// The concept's section 02, drawn. Seven beats, and the two that do not exist at
// Outform today are the gates: the third draft that is honestly refused, and the
// one question at close that makes the ledger worth keeping.

const JOURNEY: Diagram = (() => ({
  key: "outform-ux-journey",
  title: "A brief goes in, an estimate comes out",
  subtitle:
    "The concept's seven beats, as the salesperson lives them · nothing on a screen edits — every affordance asks",
  cols: [
    {
      root: box("j-who", undefined, "col", [person("j-sales", "Salesperson"), person("j-est", "Estimator")], {
        pad: 0,
        gap: 40,
      }),
    },
    {
      root: box(
        "j-in",
        "1 · THE BRIEF",
        "col",
        [
          leaf("j-b1", "Notes, sketch, a photo", "[whatever the client gave]", "Mail"),
          leaf("j-b2", "Handed to the chat", "[there is no form]", "MessageSquare"),
        ],
        { depth: 0 },
      ),
      align: "top",
    },
    {
      root: box(
        "j-talk",
        "2 · A SHORT CONVERSATION",
        "col",
        [
          leaf("j-t1", "Agent asks what is unknown", "[units · finish · window]", "MessagesSquare"),
          leaf("j-t2", "Enough to settle it", "[a moment, not a meeting]", "Clock"),
        ],
        { depth: 0 },
      ),
      align: "top",
    },
    {
      root: box(
        "j-work",
        "3 · THE AGENT WORKS",
        "col",
        [
          leaf("j-w1", "Reads the skill", "[their method, written down]", "BookOpen"),
          leaf("j-w2", "Opens the connectors", "[history · D365 · vendors]", "Plug"),
          leaf("j-w3", "Every number in SQL", "[the model only phrases it]", "Calculator"),
        ],
        { depth: 0 },
      ),
      align: "top",
    },
    {
      root: box(
        "j-out",
        "4 · THREE DRAFTS",
        "col",
        [
          leaf("j-o1", "Three real builds", "[each its own price]", "Layers"),
          leaf("j-o2", "Each with its why", "[one short paragraph]", "Quote"),
          leaf("j-o3", "Or two, and it says so", "[no straw third option]", "CircleAlert", "gate"),
        ],
        { depth: 0 },
      ),
      align: "top",
    },
    {
      root: box(
        "j-read",
        "5 · TAKEN APART",
        "col",
        [
          leaf("j-r1", "The PDF on the left", "[what a salesperson sends]", "FileText"),
          leaf("j-r2", "The reasoning beside it", "[block by block]", "PanelRight"),
          leaf("j-r3", "Each number traced back", "[to what produced it]", "Search"),
        ],
        { depth: 0 },
      ),
      align: "top",
    },
    {
      root: box(
        "j-back",
        "6 · SENT BACK",
        "col",
        [
          leaf("j-k1", "Any section, from where it sits", "[not a fresh request]", "CornerUpLeft"),
          leaf("j-k2", "That block is reworked", "[the rest stands]", "RefreshCw"),
        ],
        { depth: 0 },
      ),
      align: "top",
    },
    {
      root: box(
        "j-led",
        "7 · RECORDED",
        "col",
        [
          leaf("j-l1", "Quoted · chosen · outcome", "[the ledger]", "Database"),
          leaf("j-l2", "Did the client push back?", "[the one question]", "HelpCircle", "gate"),
        ],
        { depth: 0 },
      ),
      align: "top",
    },
  ],
  wires: [
    { a: "j-sales", b: "j-b1", as: "r", bs: "l", num: 1 },
    { a: "j-b1", b: "j-b2", as: "b", bs: "t" },
    { a: "j-b2", b: "j-t1", as: "r", bs: "l", num: 2 },
    { a: "j-t1", b: "j-t2", as: "b", bs: "t" },
    { a: "j-t2", b: "j-w1", as: "r", bs: "l", num: 3 },
    { a: "j-w1", b: "j-w2", as: "b", bs: "t" },
    { a: "j-w2", b: "j-w3", as: "b", bs: "t" },
    { a: "j-w3", b: "j-o1", as: "r", bs: "l", num: 4 },
    { a: "j-o1", b: "j-o2", as: "b", bs: "t" },
    { a: "j-o2", b: "j-r1", as: "r", bs: "l", num: 5 },
    { a: "j-r1", b: "j-r2", as: "b", bs: "t" },
    { a: "j-r2", b: "j-r3", as: "b", bs: "t" },
    { a: "j-r3", b: "j-k1", as: "r", bs: "l", num: 6 },
    { a: "j-k1", b: "j-w2", as: "t", bs: "b", label: "the agent goes back to the stores", dashed: true },
    { a: "j-k1", b: "j-k2", as: "b", bs: "t" },
    { a: "j-est", b: "j-r1", as: "r", bs: "l", label: "reviews the same sheet" },
    { a: "j-k2", b: "j-l1", as: "r", bs: "l", num: 7 },
    { a: "j-l1", b: "j-l2", as: "b", bs: "t" },
    { a: "j-l2", b: "j-w2", as: "t", bs: "b", label: "and the next quote is better for it", red: true },
  ],
  legend: [
    { style: "solid", label: "one brief, in order" },
    { style: "dash", label: "a section sent back re-enters at the work, not at the beginning" },
    { style: "red", label: "the loop that does not exist at Outform today — their §6 job record does not store an outcome" },
  ],
  padBottom: 86,
}))();

// ═══ THE DEMONSTRATION JOB ═══════════════════════════════════════════════════
// One brief, three genuinely different builds. Not one build at three margins —
// the concept is explicit that a straw option is worse than two honest ones.

type BomLine = { label: string; detail: string; amount: number; stale?: boolean };
type Comp = { id: string; client: string; year: number; units: number; unit: number; sim: number };

type Draft = {
  id: string;
  name: string;
  method: string;
  units: number;
  unit: number;
  cost: number;
  leadDays: number;
  route: string;
  why: string;
  bom: BomLine[];
  comps: Comp[];
  /** Named exposure that the build-up alone would not surface. */
  flag?: string;
};

const BRIEF = {
  client: "Ulta Beauty",
  job: "Fragrance endcap refresh · 240 stores",
  band: "Structured / RFP",
  bandLo: 45,
  bandHi: 50,
  windowDays: 87,
  windowDate: "6 October 2026",
  statedBudget: 650_000,
};

const DRAFTS: Draft[] = [
  {
    id: "D1",
    name: "Powder-coated steel, built in Dallas",
    method: "Domestic build · 14ga steel, powder coat, acrylic header",
    units: 240,
    unit: 1842,
    cost: 442_080,
    leadDays: 38,
    route: "Dallas → 42 stores/week rollout",
    why:
      "The only build with real slack against the reset window, and the one that needs no ocean booking. It is also 40% more per unit than anything else on this sheet, and that difference is the whole decision.",
    bom: [
      { label: "Steel frame, 14ga, powder coat", detail: "$776/unit · rate 11 days old", amount: 186_240 },
      { label: "Acrylic header + graphics", detail: "$296/unit · rate 9 days old", amount: 71_040 },
      { label: "LED strip + driver", detail: "$220/unit · rate 41 days old", amount: 52_800, stale: true },
      { label: "Shelving + brackets", detail: "$184/unit", amount: 44_160 },
      { label: "Assembly labour, 3.4 h/unit", detail: "$72/h, Dallas", amount: 58_752 },
      { label: "Pack + kit", detail: "$68/unit", amount: 16_320 },
      { label: "Inbound freight", detail: "$53/unit · rate 34 days old", amount: 12_768, stale: true },
    ],
    comps: [
      { id: "DEMO-1194", client: "Ulta", year: 2025, units: 180, unit: 1910, sim: 0.81 },
      { id: "DEMO-0977", client: "Sephora", year: 2024, units: 300, unit: 1733, sim: 0.74 },
      { id: "DEMO-1341", client: "Ulta", year: 2026, units: 120, unit: 2040, sim: 0.69 },
    ],
  },
  {
    id: "D2",
    name: "Steel and acrylic, built in Vietnam",
    method: "Offshore build · same fixture, existing tooling from DEMO-1194",
    units: 240,
    unit: 1318,
    cost: 316_320,
    leadDays: 74,
    route: "Haiphong → Long Beach → Dallas DC",
    why:
      "The build Outform have actually run for this client, twice. The tooling exists, so the cost is a repeat rather than an estimate, and it lands 13 days before the window opens.",
    bom: [
      { label: "Steel frame, 14ga, powder coat", detail: "$510/unit · Vietnam rate, 18 days old", amount: 122_400 },
      { label: "Acrylic header + graphics", detail: "$244/unit", amount: 58_560 },
      { label: "LED strip + driver", detail: "$196/unit · rate 41 days old", amount: 47_040, stale: true },
      { label: "Shelving + brackets", detail: "$140/unit", amount: 33_600 },
      { label: "Assembly labour, 2.9 h/unit", detail: "$31/h, Haiphong", amount: 21_600 },
      { label: "Ocean freight + duty", detail: "$112/unit · MFN duty-free", amount: 26_880 },
      { label: "Pack + kit", detail: "$26/unit", amount: 6_240 },
    ],
    comps: [
      { id: "DEMO-1194", client: "Ulta", year: 2025, units: 180, unit: 1368, sim: 0.88 },
      { id: "DEMO-1052", client: "Ulta", year: 2024, units: 260, unit: 1291, sim: 0.83 },
      { id: "DEMO-0977", client: "Sephora", year: 2024, units: 300, unit: 1240, sim: 0.71 },
    ],
  },
  {
    id: "D3",
    name: "Aluminium extrusion and printed film",
    method: "Offshore build · lighter frame, film finish instead of powder coat",
    units: 240,
    unit: 1096,
    cost: 263_040,
    leadDays: 68,
    route: "Haiphong → Long Beach → Dallas DC",
    why:
      "The only draft that reads as a different fixture rather than a cheaper version of the same one. It is also the least evidenced: no job in the library carries a printed-film finish for this client.",
    flag:
      "Section 232, effective 6 April 2026, charges 50% on the FULL customs value of covered metal articles — aluminium extrusion is covered, and the old metal-content basis is gone. Every pre-April comparable understates this build's landed cost. The $32,160 line below carries the new basis; the comparables do not.",
    bom: [
      { label: "Aluminium extrusion frame", detail: "$370/unit · rate 6 days old", amount: 88_800 },
      { label: "Printed film + laminate", detail: "$184/unit · no comparable", amount: 44_160 },
      { label: "LED strip + driver", detail: "$196/unit · rate 41 days old", amount: 47_040, stale: true },
      { label: "Shelving + brackets", detail: "$120/unit", amount: 28_800 },
      { label: "Assembly labour, 2.1 h/unit", detail: "$31/h, Haiphong", amount: 15_840 },
      { label: "Ocean freight + §232 duty", detail: "$134/unit · 50% on full value", amount: 32_160 },
      { label: "Pack + kit", detail: "$26/unit", amount: 6_240 },
    ],
    comps: [
      { id: "DEMO-1420", client: "Lush", year: 2025, units: 90, unit: 1044, sim: 0.62 },
      { id: "DEMO-1052", client: "Ulta", year: 2024, units: 260, unit: 1291, sim: 0.58 },
    ],
  },
];

const money = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;
const price = (cost: number, marginPct: number) => cost / (1 - marginPct / 100);

// ═══ COST LIVES IN THE BOX, PRICE LIVES ON THE PROPOSAL ══════════════════════
// Erez, 15 August: "when we look at line items, I don't believe they're going to
// have the cost in the actual estimate — not in the ballpark, not in the detail.
// They're going to have the price. They need to also be able to see the cost. We
// need to put the cost in the boxes so we don't have to create another view."
//
// That one rule removes a whole view. We had been heading for a fourth tab — a
// Draft, their Excel — because there was nowhere to see cost or touch a margin.
// There already was: the reasoning column. The lever belongs on the thing it
// changes, and the number's reason is the right place to change the number.
//
// It also settles the §3.2 question underneath it. Their document asks for margin
// applied per component — visibly keen on the lines a client can price for
// themselves, protected on the lines they cannot — and simultaneously promises
// the client an explainable breakdown. Those only conflict while cost and price
// share a page. Split them and the conflict is gone: the estimate carries price,
// which is what a client is owed, and the allocation happens in a column a client
// never sees.

/** A line's own margin, defaulting to the estimate's. */
const lineMargin = (label: string, m: number, lineM: Record<string, number>) =>
  lineM[label] ?? m;

/** What a line SELLS for. The estimate only ever shows this. */
const linePrice = (b: { label: string; amount: number }, m: number, lineM: Record<string, number>) =>
  price(b.amount, lineMargin(b.label, m, lineM));

/** The estimate's price is the sum of its lines — so moving ONE line moves the
 *  headline. With no line overridden this is exactly price(d.cost, m); the two
 *  can never disagree, which is the only reason it is safe to show both. */
const sheetPrice = (d: Draft, m: number, lineM: Record<string, number>) =>
  d.bom.reduce((s, b) => s + linePrice(b, m, lineM), 0);

// ═══ THE SECTIONS OF THE PROPOSAL ════════════════════════════════════════════
// Ballpark first, then detail — the toggle on the sheet adds the last three
// rather than navigating anywhere. That is Erez's sequence: "first the ballpark
// and then the detail", one document that deepens, not two documents.

type SecId = "brief" | "build" | "number" | "comps" | "lead" | "bom" | "sourcing" | "validity" | "refusal";

/** What the Costing Template is waiting on, for THIS estimate.
 *
 *  Four states and they are deliberately not a progress bar: `history` needed no
 *  RFP at all, `quoted` came back, `waiting` is out with vendors, and `no price`
 *  is the one the estimate cannot stand on. Their §3.2 already asks us to
 *  "highlight missing cost risk" — this is that flag with a closing state. */
const SOURCING: { line: string; state: "history" | "quoted" | "waiting" | "missing"; asked: string; returned: string }[] = [
  { line: "Steel frame, 14ga, powder coat", state: "quoted",  asked: "3 vendors · 14 Aug", returned: "16 Aug" },
  { line: "Acrylic header + graphics",      state: "history", asked: "—",                  returned: "—" },
  { line: "LED strip + driver",             state: "waiting", asked: "2 vendors · 15 Aug", returned: "3 days out" },
  { line: "Shelving + brackets",            state: "history", asked: "—",                  returned: "—" },
  { line: "Custom hinge, spec not final",   state: "missing", asked: "not sent",           returned: "—" },
];

// ⛔ WHAT IS ON THE SHEET IS WHAT A CLIENT MAY READ (Erez, 18 Aug).
// "The centre page is the estimate that is basically ready to send to the
//  client… on the left panel is where we put all the analysis, all the
//  considerations, all the variations."
//
// So `comps` and `sourcing` are NOT here. Comparable jobs are other clients'
// job numbers, unit prices and similarity scores; sourcing names our vendors and
// says one of them quotes 22.8% above market. Both are the reasoning BEHIND the
// price and neither can survive being forwarded. They live in the rail.
const CLIENT_BALLPARK: SecId[] = ["brief", "build", "number", "lead", "refusal"];
const CLIENT_DETAILED: SecId[] = ["brief", "build", "number", "bom", "lead", "validity", "refusal"];

const SEC_TITLE: Record<SecId, string> = {
  brief: "What we read",
  build: "The build",
  number: "The number",
  comps: "Comparable jobs",
  bom: "Price by component",
  sourcing: "Sourcing and vendor exposure",
  lead: "Lead time and route",
  validity: "Validity and assumptions",
  refusal: "What we could not price",
};

type Reason = {
  searched: string;
  found: string;
  concluded: string;
  open?: string;
  sources: string[];
  /** Only one card carries the what-if bar — the one that owns the price. */
  lever?: boolean;
  /** WHICH WRITTEN RULE THIS SECTION APPLIED. Added 18 Aug after §2.1: "two
   *  estimators given the same brief produce materially different numbers
   *  because THERE IS NO STANDARDIZED METHODOLOGY." The whole product is that
   *  standard made explicit — so every section names the rule it followed, and
   *  an estimator who disagrees now has something specific to argue with
   *  instead of a number. */
  standard?: string;
  /** WHAT WOULD MOVE THIS. A reason that cannot say what would change it is a
   *  verdict, not reasoning. */
  changes?: string;
};

/** The reset window is STATE, not a constant: the chat can move it, and when it
 *  does, the sheet and the reasoning both have to move with it. That is the whole
 *  demonstration — a sentence in the conversation changes the document. */
type Win = { days: number; date: string };
const WIN_OCT: Win = { days: BRIEF.windowDays, date: BRIEF.windowDate };
const WIN_NOV: Win = { days: 118, date: "6 November 2026" };

function reasonFor(id: SecId, d: Draft, m: number, win: Win): Reason {
  const p = price(d.cost, m);
  switch (id) {
    case "brief":
      return {
        standard: "Classify before you price. The band is chosen from the brief, and the cost is not looked at until it is chosen — see the rule below on Structured/RFP.",
        changes: "A finish specified in writing, or a firm store count. Both are assumptions on this sheet and both move the band.",
        searched: "The inbound email and its sketch, the Ulta account in Sugar CRM, and the last four jobs for this client.",
        found: `240 stores, fragrance category, a reset window opening ${win.date}. No finish specified. No budget in writing — ${money(BRIEF.statedBudget)} was said on the call and is recorded as stated, not agreed.`,
        concluded: `Structured / RFP. It arrived as a scoped request with a deadline attached, which is the band definition in their own §5 — target margin ${BRIEF.bandLo}–${BRIEF.bandHi}%.`,
        open: "The finish is unspecified. That is why this is three drafts and not one recommendation.",
        sources: ["sugar_crm.accounts", "jobs · 4 prior for this client", "pv_bands · §5"],
      };
    case "build":
      return {
        searched: "1,483 decided jobs, filtered to fragrance fixtures for beauty retail between 100 and 400 units.",
        found: "31 jobs. Eleven domestic, eighteen Vietnam, two Mexico. Nine carry a finish this brief could take.",
        concluded: d.why,
        open:
          d.id === "D3"
            ? "No job in the library carries a printed-film finish for this client, so this draft's finish cost is a build-up and not a repeat."
            : undefined,
        standard: "Price every manufacturing route, then let the deadline eliminate. The build is chosen against the window, not against the cheapest unit cost.",
        changes: "A firm finish specification. Three of the five routes price differently once the finish is fixed.",
        sources: ["jobs · 1,483 decided", "job_assets · drawings re-ranked", "reference_rates · §10.1"],
      };
    case "number":
      return {
        searched: "The build-up below, then the three nearest comparable jobs as a check on it.",
        found: `Our cost ${money(d.cost)} — ${money(d.unit)} a unit. The comparables sit at ${money(
          Math.min(...d.comps.map((c) => c.unit)),
        )}–${money(Math.max(...d.comps.map((c) => c.unit)))} a unit.`,
        concluded: `Cost is the build-up, never a comparable. The comparables only say whether the build-up is plausible, and here they bracket it. At ${m}% the price is ${money(
          p,
        )}.`,
        open: `Outside ${BRIEF.bandLo}–${BRIEF.bandHi}% you are outside the band Outform published for this class of work. The system still computes it, and says so on the face of the estimate.`,
        standard: "Cost is the build-up. A comparable can say a build-up is implausible; it can never BE the cost. The margin comes from the published band for the class of work, not from what this client paid last time.",
        changes: "A returned vendor quote on any unpriced line, or a reclassification out of Structured/RFP. The first moves cost, the second moves the band.",
        sources: ["estimate_lines", "pv_bands · §5", "comps · similarity ≥ 0.55"],
        lever: true,
      };
    case "comps":
      return {
        searched: "The decided-job corpus on the structured job vector — type, band, vertical, unit count, finish, budget proximity.",
        found: `${d.comps.length} jobs above the 0.55 similarity floor. The drawing was used to re-rank them and to collapse two revisions of the same job into one.`,
        concluded:
          "The drawing never sets the price. Geometry-only prediction ran 56% error on 503 real timed jobs against 44% once structured attributes were added — so visual similarity is how an estimator finds a job, not how the number is made.",
        open:
          d.comps.length < 3
            ? "Fewer than three comparables cleared the floor. The estimate says so rather than padding the list with weak matches."
            : undefined,
        standard: "Decided jobs only, above a 0.55 similarity floor, and three of them or the estimate says it could not find three. A comparable at 0.2 pads the requirement; it does not support the number.",
        changes: "Loading the lost jobs. Every comparable here is a job Outform WON, which tells us a price was acceptable and never that it was optimal.",
        sources: ["jobs · decided only", "job_assets · vector(1536)", "estimate_comps"],
      };
    case "bom":
      return {
        searched: "Every rate in §10.1, each at its effective date rather than at today's date.",
        found: `${d.bom.length} lines. ${d.bom.filter((b) => b.stale).length} of them are priced from a rate older than the 30-day target in their §12.1.`,
        concluded:
          "A stale rate is used and flagged, never silently refreshed. The alternative — quietly re-pricing at today's rate — makes two estimates written a week apart incomparable for no reason the reader can see.",
        open: "The LED driver rate is 41 days old and is the largest single line running on stale data.",
        standard: "A rate is used at its effective date and its age is shown. Nothing is silently re-priced at today's rate.",
        changes: "A refreshed LED driver rate. It is 41 days old against their §12.1 target of 95% under 30 days, and it is the largest line running stale.",
        sources: ["reference_tables · 7 of §10.1", "reference_rates · effective_date", "vendor_quotes"],
      };
    case "sourcing":
      return {
        searched: "Vendor scorecards and the last blind RFQ round for each line above $30,000.",
        found:
          "Powder coat has one qualified vendor. Their standing quote is 22.8% above what the market returned on an anonymous RFQ for the same specification.",
        concluded:
          "The exposure is named on the estimate rather than absorbed into the cost. One line item pays for the anonymous-RFQ round on its own.",
        open: "No vendor on this job has any delivery history recorded, so a cheap quote from an unreliable vendor is indistinguishable from a cheap quote from a reliable one.",
        standard: "Ask only for what history cannot price. The Costing Template goes out for the gaps, not for the whole bill of materials — and the one line still without a price is excluded from the total rather than guessed at.",
        changes: "The LED harness quote, which is three days out with two vendors. The custom hinge cannot be asked for at all until its specification is final.",
        sources: ["vendors", "rfqs · blind round", "vendor_scorecard · empty"],
      };
    case "lead":
      return {
        searched: "Every manufacturing route priced, then filtered against the reset window.",
        found: `${d.leadDays} days on this route. The window opens in ${win.days} days, leaving ${
          win.days - d.leadDays
        } days of slack.`,
        concluded:
          "Any route whose lead time exceeds the deadline is excluded and the exclusion is stated on the estimate — an option removed silently is an option the salesperson will be asked about in the room.",
        open: undefined,
        standard: "Every route is priced and any route that misses the deadline is excluded WITH the exclusion stated. An option removed silently is an option the salesperson gets asked about in the room.",
        changes: "A later reset window. Moving it to November puts two excluded routes back in play and takes roughly 9% out of cost.",
        sources: ["routes · 5 priced", "jobs · historical transit", "reference_rates · freight"],
      };
    case "validity":
      return {
        searched: "The volatility of each input against the last twelve months of the reference tables.",
        found:
          "Steel is the volatile input. A 5% move in the hot-rolled index moves this draft by roughly 2.1% of cost; nothing else moves it by more than 0.6%.",
        concluded: "Valid 30 days. After that the sheet is recomputed rather than re-signed.",
        standard: "An estimate states what it is true AS OF. Quotes arrive over days, so a total without a timestamp is two different numbers to two people.",
        changes: "Any returned vendor quote resets the as-of and recomputes the total. The sheet is recomputed, never re-signed.",
        sources: ["reference_rates · 12 months", "market · deviation > 15% flagged"],
      };
    case "refusal":
      return {
        searched: "Everything the §3.7.1 conversion score asks for.",
        found:
          "Outcome, price position and client behaviour are 80% of the weight of their own scoring model, and their §6 job record stores none of them. It does not record whether a job was won.",
        concluded:
          "The score is not computed and not estimated. Three columns — quoted price, final price, did the client push back — and one question at close would turn it on, and would be worth more than the model.",
        open:
          "Every comparable on this sheet is a DEMO- record. The agent will not attach a real outcome to fabricated data and says so before it is asked.",
        standard: "Where the evidence is missing the system refuses and says why. It does not estimate a score it cannot compute.",
        changes: "⭐ Their §1 names SUGAR CRM as an integration and their §6 already pulls Sugar Scores — and Sugar carries won and lost on every opportunity by default. So the outcome data is most likely UNJOINED rather than missing. One join from an opportunity to a job number may turn this section on without anyone entering a thing.",
        sources: ["field_events · all unverified", "jobs · outcome column absent", "sugar · win/loss, unjoined"],
      };
  }
}

// ═══ SMALL PARTS ═════════════════════════════════════════════════════════════

const T = {
  ink: N.ink,
  ink2: N.ink2,
  ink3: N.ink3,
  line: N.line,
  lineHard: N.lineStrong,
  paper: "#FFFFFF",
  desk: "#F1F0EC",
  chrome: "#FBFBFA",
  accent: N.blue,
  accentBg: N.blueBg,
  warn: N.orange,
  warnBg: N.orangeBg,
  stop: N.red,
  stopBg: N.redBg,
  good: N.green,
  goodBg: N.greenBg,
};

function Chip({ children, tone = "gray" }: { children: React.ReactNode; tone?: "gray" | "blue" | "amber" | "red" | "green" }) {
  const map = {
    gray: [N.gray, N.grayBg],
    blue: [N.blue, N.blueBg],
    amber: [N.orange, N.orangeBg],
    red: [N.red, N.redBg],
    green: [N.green, N.greenBg],
  } as const;
  const [fg, bg] = map[tone];
  return (
    <span
      style={{
        fontFamily: MONO,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: ".06em",
        textTransform: "uppercase",
        color: fg,
        background: bg,
        borderRadius: R_CTL,
        padding: "2px 6px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Money({ v, size = 26, color = T.ink }: { v: number; size?: number; color?: string }) {
  return (
    <span style={{ fontFamily: MONO, fontSize: size, fontWeight: 600, color, letterSpacing: "-.02em" }}>{money(v)}</span>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: MONO,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: ".1em",
        textTransform: "uppercase",
        color: T.ink3,
      }}
    >
      {children}
    </div>
  );
}

/** The one control that changes a number. A what-if — it stores nothing. */
function MarginBar({ m, setM }: { m: number; setM: (n: number) => void }) {
  const inBand = m >= BRIEF.bandLo && m <= BRIEF.bandHi;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
        <Label>margin · what-if</Label>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: inBand ? T.good : T.warn }}>
          {m}% {inBand ? "in band" : "outside the band"}
        </span>
      </div>
      <div style={{ position: "relative", height: 6, borderRadius: 3, background: "#E6E5E1" }}>
        {/* the published band, drawn where it actually sits on the 35–70 scale */}
        <div
          style={{
            position: "absolute",
            left: `${((BRIEF.bandLo - 35) / 35) * 100}%`,
            width: `${((BRIEF.bandHi - BRIEF.bandLo) / 35) * 100}%`,
            top: 0,
            bottom: 0,
            background: T.goodBg,
            border: `1px solid ${T.good}`,
            borderRadius: 3,
          }}
        />
      </div>
      <input
        type="range"
        min={35}
        max={70}
        step={0.5}
        value={m}
        onChange={(e) => setM(parseFloat(e.target.value))}
        style={{ width: "100%", marginTop: -4, accentColor: T.accent }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", fontFamily: MONO, fontSize: 10, color: T.ink3 }}>
        <span>35%</span>
        <span>
          published band {BRIEF.bandLo}–{BRIEF.bandHi}%
        </span>
        <span>70%</span>
      </div>
    </div>
  );
}

// ═══ THE REASONING CARD ══════════════════════════════════════════════════════
// Five parts, and the fifth is the one that makes it worth reading: what is
// still open. A reasoning panel that only ever justifies is marketing.

/** Two taps, one point at a time. A slider per line would be twenty sliders. */
function Step({ v, onChange }: { v: number; onChange: (n: number) => void }) {
  const btn: React.CSSProperties = {
    width: 18,
    height: 18,
    lineHeight: 1,
    fontFamily: F,
    fontSize: 12,
    fontWeight: 600,
    color: T.ink2,
    background: T.paper,
    border: `1px solid ${T.line}`,
    borderRadius: 4,
    cursor: "pointer",
    padding: 0,
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <button style={btn} onClick={() => onChange(Math.max(0, v - 1))}>
        −
      </button>
      <span style={{ fontFamily: MONO, fontSize: 11.5, color: T.ink, minWidth: 26, textAlign: "center" }}>{v}%</span>
      <button style={btn} onClick={() => onChange(Math.min(90, v + 1))}>
        +
      </button>
    </span>
  );
}

/** THE COST VIEW. It exists in exactly one place in the whole product, and this
 *  is it — cost per line, the margin that line carries, and what it therefore
 *  sells for. Their §3.2 in one table: keen where the client can check, held
 *  where they cannot. Nothing here is on the document the client receives. */
function CostLines({
  d,
  m,
  lineM,
  setLine,
}: {
  d: Draft;
  m: number;
  lineM: Record<string, number>;
  setLine?: (label: string, pct: number) => void;
}) {
  const td: React.CSSProperties = { padding: "5px 6px", borderBottom: `1px solid ${T.line}`, verticalAlign: "middle" };
  const moved = d.bom.filter((b) => lineM[b.label] !== undefined && lineM[b.label] !== m).length;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <Label>cost, margin and price by line</Label>
        <Chip tone="red">not on the estimate</Chip>
      </div>

      {/* ── WHY THIS COLUMN IS NOT THEIR COLUMN (18 August 2026) ──────────────
          Read their §3.2 against the workbook Zahid actually sent and the two
          disagree. §3.2 asks for margin applied per component — keen on the
          lines a client can price for themselves, protected on the lines they
          cannot. Their parts breakout computes every line as
          `part cost × 4.37115324836804`: one literal, on every row, the job's
          total loaded cost allocated back in proportion to what each part
          already cost.

          A uniform multiplier cannot express a per-component strategy — it is
          uniform by construction. And because it is proportional, the parts a
          client can most easily check carry exactly the same 337% uplift as the
          bespoke metalwork, so the breakdown is least defensible precisely where
          the client is most able to check it.

          The cost in this table is the line's OWN cost — what leaves if the line
          leaves. That is what makes a per-line margin a decision an estimator can
          defend, and it is the prerequisite for §3.2 rather than a nicety. */}
      <div style={{ fontSize: 10.5, color: T.ink3, marginBottom: 8, lineHeight: 1.45 }}>
        This is each line&rsquo;s <b style={{ color: T.ink2 }}>own</b> cost — what actually leaves if the
        line leaves. Their breakout allocates the job total across parts at a flat{" "}
        <span style={{ fontFamily: MONO }}>×4.371</span>, so a $3.96 part reads as $17.31 to a client
        deciding what to cut. Per-line margin only means something on top of this column.
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
        <thead>
          <tr>
            {["line", "cost", "margin", "price"].map((h, i) => (
              <th
                key={h}
                style={{
                  textAlign: i === 0 ? "left" : "right",
                  padding: "0 6px 4px",
                  fontFamily: MONO,
                  fontSize: 9,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  color: T.ink3,
                  borderBottom: `1px solid ${T.line}`,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {d.bom.map((b) => {
            const lm = lineMargin(b.label, m, lineM);
            const off = lm !== m;
            return (
              <tr key={b.label}>
                <td style={{ ...td, color: T.ink }}>
                  {b.label}
                  {off && (
                    <div style={{ fontSize: 10, color: T.accent, marginTop: 1 }}>
                      moved from {m}% by an estimator
                    </div>
                  )}
                </td>
                <td style={{ ...td, textAlign: "right", fontFamily: MONO, color: T.ink2, whiteSpace: "nowrap" }}>
                  {money(b.amount)}
                </td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                  {setLine ? <Step v={lm} onChange={(n) => setLine(b.label, n)} /> : `${lm}%`}
                </td>
                <td
                  style={{
                    ...td,
                    textAlign: "right",
                    fontFamily: MONO,
                    color: off ? T.accent : T.ink,
                    whiteSpace: "nowrap",
                  }}
                >
                  {money(linePrice(b, m, lineM))}
                </td>
              </tr>
            );
          })}
          <tr>
            <td style={{ padding: "7px 6px", fontWeight: 600, color: T.ink }}>Total</td>
            <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: MONO, fontWeight: 600, color: T.ink2 }}>
              {money(d.cost)}
            </td>
            <td />
            <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: MONO, fontWeight: 600, color: T.ink }}>
              {money(sheetPrice(d, m, lineM))}
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: 8, fontSize: 11, lineHeight: 1.55, color: T.ink3 }}>
        {moved > 0 ? (
          <>
            <strong style={{ color: T.accent }}>
              {moved} line{moved > 1 ? "s" : ""} moved off {m}%.
            </strong>{" "}
            The estimate's total moved with them and every line still shows what it was computed at. Nothing is saved
            until you tell the agent — and when you do, the pair goes on the Summary with your name on it.
          </>
        ) : (
          <>
            Their §3.2, in their words: be visibly competitive on the lines a client can price themselves, and hold
            margin on the ones they cannot. Move a line and the estimate's total moves; nothing is saved until you tell
            the agent.
          </>
        )}
      </div>
    </div>
  );
}

function ReasonCard({
  id,
  d,
  m,
  win,
  setM,
  active,
  onSend,
  lineM,
  setLine,
}: {
  id: SecId;
  d: Draft;
  m: number;
  win: Win;
  setM: (n: number) => void;
  active: boolean;
  onSend?: () => void;
  /** Per-line margins. The estimate never shows these; this column is the only
   *  place they exist, and the only place cost appears at all. */
  lineM?: Record<string, number>;
  setLine?: (label: string, pct: number) => void;
}) {
  const r = reasonFor(id, d, m, win);
  return (
    <div
      style={{
        background: T.paper,
        border: `1px solid ${active ? T.accent : T.line}`,
        boxShadow: active ? `0 0 0 3px ${T.accentBg}` : "none",
        borderRadius: R,
        padding: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.ink }}>{SEC_TITLE[id]}</div>
        <Chip tone={id === "refusal" ? "red" : "blue"}>why</Chip>
      </div>

      {r.standard && (
        <div
          style={{
            marginBottom: 14, padding: "9px 12px", borderRadius: R,
            background: N.bgGray, border: `1px solid ${T.line}`,
          }}
        >
          <Label>the rule applied</Label>
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: T.ink2, marginTop: 4 }}>{r.standard}</div>
        </div>
      )}

      <Row k="Searched" v={r.searched} />
      <Row k="Found" v={r.found} />
      <Row k="Concluded" v={r.concluded} strong />
      {r.changes && <Row k="Would change" v={r.changes} />}
      {r.open && <Row k="Still open" v={r.open} tone="amber" />}

      {/* Cost appears in this column and nowhere else. The estimate carries the
          price; the reason for the price carries what it cost us. */}
      {id === "number" && (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            background: N.bgGray,
            border: `1px solid ${T.line}`,
            borderRadius: R,
            padding: "9px 11px",
          }}
        >
          <div>
            <Label>our cost</Label>
            <div style={{ marginTop: 2 }}>
              <Money v={d.cost} size={19} color={T.ink} />
            </div>
          </div>
          <Chip tone="red">not on the estimate</Chip>
        </div>
      )}

      {r.lever && <MarginBar m={m} setM={setM} />}

      {id === "bom" && lineM && <CostLines d={d} m={m} lineM={lineM} setLine={setLine} />}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 12 }}>
        {r.sources.map((s) => (
          <span
            key={s}
            style={{
              fontFamily: MONO,
              fontSize: 11,
              color: T.ink2,
              background: N.bgGray,
              border: `1px solid ${T.line}`,
              borderRadius: R_CTL,
              padding: "2px 6px",
            }}
          >
            {s}
          </span>
        ))}
      </div>

      {onSend && (
        <button
          onClick={onSend}
          style={{
            marginTop: 12,
            width: "100%",
            fontFamily: F,
            fontSize: 12,
            fontWeight: 500,
            color: T.accent,
            background: T.accentBg,
            border: `1px solid ${T.accent}22`,
            borderRadius: R_CTL,
            padding: "7px 10px",
            cursor: "pointer",
          }}
        >
          Send this section back to the agent →
        </button>
      )}
    </div>
  );
}

function Row({ k, v, strong, tone }: { k: string; v: string; strong?: boolean; tone?: "amber" }) {
  return (
    <div style={{ display: "flex", gap: 12, marginBottom: 11 }}>
      <div style={{ flex: "0 0 74px" }}>
        <Label>{k}</Label>
      </div>
      <div
        style={{
          flex: 1,
          fontSize: 14,
          lineHeight: 1.6,
          color: tone === "amber" ? T.warn : strong ? T.ink : T.ink2,
          fontWeight: strong ? 500 : 400,
        }}
      >
        {v}
      </div>
    </div>
  );
}


// ═══ ANALYSIS · RAIL ONLY ════════════════════════════════════════════════════
// None of this may appear on the sheet. Every block here is either a fact about
// US (what it cost, what band we applied, whose budget we are aiming at) or a
// fact about SOMEBODY ELSE (another client's job, a vendor's quote). Both are
// unforwardable, and that is the whole test for which side of the screen a thing
// belongs on.

/** Their §1's three pricing views. In the RAIL, not on the estimate — a "view"
 *  is something the estimator reads, and all three are facts about us. */
function ThreeViews({ d, m }: { d: Draft; m: number }) {
  const bandPrice = price(d.cost, (BRIEF.bandLo + BRIEF.bandHi) / 2);
  const p = price(d.cost, m);
  const fits = p <= BRIEF.statedBudget;
  const marginToFit = (1 - d.cost / BRIEF.statedBudget) * 100;
  const views = [
    { label: "our cost", value: d.cost, sub: "the internal baseline", color: T.ink2 },
    { label: "perceived value price", value: bandPrice, sub: `band midpoint · ${BRIEF.bandLo}–${BRIEF.bandHi}%`, color: T.ink },
    {
      label: "target price scenario",
      value: BRIEF.statedBudget,
      sub: fits ? `their budget — holds at ${m}%` : `their budget — needs ${marginToFit.toFixed(1)}%`,
      color: fits ? T.good : T.warn,
    },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {views.map((v) => (
        <div key={v.label} style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <Label>{v.label}</Label>
            <div style={{ fontSize: 12, color: T.ink3, marginTop: 3 }}>{v.sub}</div>
          </div>
          <Money v={v.value} size={22} color={v.color} />
        </div>
      ))}
      <div style={{ fontFamily: MONO, fontSize: 10.5, color: T.warn, marginTop: 2 }}>
        NONE OF THESE THREE APPEARS ON THE ESTIMATE
      </div>
    </div>
  );
}

/** THE CONFIDENCE BAND — §7 and §4, and it was missing entirely until 18 August.
 *
 *  §7: "Every estimate produced by the platform carries a confidence score
 *  expressed as a band (High / Medium / Low) with contributing factors
 *  displayed," calculated from data completeness, historical match quality,
 *  model agreement, and feedback loop maturity. §4 separately requires "a
 *  rationale for the confidence score" on every estimate.
 *
 *  ⭐ THREE OF THE FOUR DRIVERS ARE COMPUTABLE ON DAY ONE. The fourth —
 *  feedback loop maturity — is genuinely zero until the system has been used,
 *  and showing that honestly is worth more than a confident band over an empty
 *  history. A score that cannot go down is not a score.
 *
 *  ⚠️ AND THE INTERVAL IS NOT INVENTED. It is the dispersion of the comps
 *  themselves: how much the historical jobs that look like this one actually
 *  disagreed. Their §12 promises ±15% and admits accuracy is "currently
 *  unmeasured" — we measure ours per estimate instead of promising one.
 *
 *  Their own purpose for it is the right one and worth quoting back: estimators
 *  use the band "to determine how much manual review an estimate requires."
 *  It is a triage signal, not a decoration. */
function ConfidenceBand({ d }: { d: Draft }) {
  const units = d.comps.map((c) => c.unit);
  const lo = Math.min(...units);
  const hi = Math.max(...units);
  const band = lo && hi ? { lo: Math.round(((lo - d.unit) / d.unit) * 100), hi: Math.round(((hi - d.unit) / d.unit) * 100) } : null;
  const best = Math.max(...d.comps.map((c) => c.sim));
  const worst = Math.min(...d.comps.map((c) => c.sim));

  const DRIVERS: { k: string; v: string; note: string; ok: boolean }[] = [
    { k: "data completeness", v: `${d.bom.length} of ${d.bom.length} lines priced`, note: "every BoM line has a source", ok: true },
    { k: "historical match quality", v: `${d.comps.length} comps · ${worst.toFixed(2)}–${best.toFixed(2)}`, note: "the weakest comp we leaned on", ok: worst >= 0.6 },
    { k: "model agreement", v: "rule and retrieval agree", note: "the arithmetic and the comps point the same way", ok: true },
    { k: "feedback loop maturity", v: "none yet", note: "no corrections recorded for this job type — and this is honest, not a placeholder", ok: false },
  ];
  const level = DRIVERS.filter((x) => x.ok).length >= 3 ? "Medium" : "Low";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 22, fontWeight: 650, color: level === "Medium" ? T.accent : T.stop }}>{level}</div>
        {band && (
          <div style={{ fontFamily: MONO, fontSize: 12, color: T.ink2 }}>
            {band.lo > 0 ? "+" : ""}{band.lo}% / {band.hi > 0 ? "+" : ""}{band.hi}% from the comps
          </div>
        )}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
        <tbody>
          {DRIVERS.map((x) => (
            <tr key={x.k}>
              <td style={{ padding: "5px 0", borderBottom: `1px solid ${T.line}`, width: 8, verticalAlign: "top" }}>
                <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: x.ok ? T.accent : T.stop, marginTop: 5 }} />
              </td>
              <td style={{ padding: "5px 8px", borderBottom: `1px solid ${T.line}`, color: T.ink }}>
                {x.k}
                <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 1 }}>{x.note}</div>
              </td>
              <td style={{ padding: "5px 0", borderBottom: `1px solid ${T.line}`, textAlign: "right", fontFamily: MONO, color: x.ok ? T.ink2 : T.stop, whiteSpace: "nowrap", verticalAlign: "top" }}>
                {x.v}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 10.5, color: T.ink3, marginTop: 9, lineHeight: 1.45 }}>
        The range is not a promise. It is how much these comparable jobs disagreed with each other.
        Their §12 targets &plusmn;15% and records the current state as <i>currently unmeasured</i>.
      </div>
    </div>
  );
}

function CompsTable({ d }: { d: Draft }) {
  return (
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr>
            {["job", "client", "year", "units", "$/unit", "similarity"].map((h, i) => (
              <th
                key={h}
                style={{
                  textAlign: i > 1 ? "right" : "left",
                  padding: "5px 8px",
                  fontFamily: MONO,
                  fontSize: 9.5,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  color: T.ink3,
                  borderBottom: `1px solid ${T.line}`,
                  fontWeight: 600,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {d.comps.map((c) => (
            <tr key={c.id}>
              <td style={{ padding: "6px 8px", fontFamily: MONO, fontSize: 12, color: T.warn, borderBottom: `1px solid ${T.line}` }}>
                {c.id}
              </td>
              <td style={{ padding: "6px 8px", color: T.ink, borderBottom: `1px solid ${T.line}` }}>{c.client}</td>
              <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: MONO, color: T.ink2, borderBottom: `1px solid ${T.line}` }}>
                {c.year}
              </td>
              <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: MONO, color: T.ink2, borderBottom: `1px solid ${T.line}` }}>
                {c.units}
              </td>
              <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: MONO, color: T.ink, borderBottom: `1px solid ${T.line}` }}>
                {money(c.unit)}
              </td>
              <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: MONO, color: c.sim >= 0.7 ? T.good : T.warn, borderBottom: `1px solid ${T.line}` }}>
                {c.sim.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
  );
}

function SourcingPanel({ d }: { d: Draft }) {
  void d;
  return (
      <div>
        <div style={{ fontSize: 14.5, lineHeight: 1.62, color: T.ink }}>
          Powder coat is the one line with a single qualified vendor, and their standing quote is{" "}
          <strong>22.8% above</strong> what the market returned on a blind RFQ for the same specification. No vendor on
          this job carries any delivery history, so a cheap quote from an unreliable vendor and a cheap quote from a
          reliable one look identical here.
        </div>

        {/* ── THE COSTING TEMPLATE'S STATE, ON THE ESTIMATE THAT NEEDS IT ─────
            Erez, 18 Aug, reading their §1.2: "there should be some tracking of
            this and it's all related to an estimate."

            Skill 7 goes OUT of the building and days pass. Anything that leaves
            and does not come straight back needs somewhere to be seen while it
            is gone, or the estimate quietly sits on a hole and nobody can tell
            the difference between a number we stand behind and one we are still
            waiting for.

            ⚠ EVERY LINE SAYS WHERE ITS NUMBER CAME FROM. That is the same rule
            as the write gate: an unverified value is visibly unverified. A price
            from history, a price from a returned quote and a price we do not
            have yet must never look alike.

            ⚠ AND THE AS-OF IS NOT DECORATION. Quotes trickle back over days, so
            the total MOVES. Two people opening this on different days would
            otherwise see different numbers with nothing explaining why — which is
            what their §3.2 "validity statement" is actually for. */}
        <div style={{ marginTop: 14, border: `1px solid ${T.line}`, borderRadius: R, overflow: "hidden" }}>
          <div
            style={{
              display: "flex", gap: 10, padding: "7px 11px", background: T.chrome,
              borderBottom: `1px solid ${T.line}`, fontFamily: MONO, fontSize: 9,
              letterSpacing: ".1em", color: T.ink3, textTransform: "uppercase",
            }}
          >
            <span style={{ flex: "1 1 auto" }}>cost line</span>
            <span style={{ width: 96, flexShrink: 0 }}>source</span>
            <span style={{ width: 150, flexShrink: 0 }}>asked</span>
            <span style={{ width: 96, flexShrink: 0 }}>returned</span>
          </div>
          {SOURCING.map((r) => (
            <div
              key={r.line}
              style={{
                display: "flex", gap: 10, padding: "8px 11px", alignItems: "baseline",
                fontSize: 13.5, color: T.ink, borderTop: `1px solid ${T.line}`,
                background: r.state === "missing" ? T.stopBg : "transparent",
              }}
            >
              <span style={{ flex: "1 1 auto", minWidth: 0 }}>{r.line}</span>
              <span style={{ width: 96, flexShrink: 0 }}>
                <Chip tone={r.state === "history" ? "gray" : r.state === "quoted" ? "green" : r.state === "waiting" ? "amber" : "red"}>
                  {r.state === "history" ? "history" : r.state === "quoted" ? "quoted" : r.state === "waiting" ? "waiting" : "no price"}
                </Chip>
              </span>
              <span style={{ width: 150, flexShrink: 0, color: T.ink2, fontSize: 12 }}>{r.asked}</span>
              <span style={{ width: 96, flexShrink: 0, color: T.ink2, fontSize: 12 }}>{r.returned}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, fontFamily: MONO, fontSize: 10.5, color: T.ink3 }}>
          AS OF 18 AUG 09:40 · one line still has no price, and the total below excludes it
        </div>
      </div>
  );
}

// ═══ THE PROPOSAL SHEET ══════════════════════════════════════════════════════
// This is the PDF. It renders identically in all three options, because the
// document is the deliverable and the options are only arguments about how its
// reasoning is surfaced.

function Section({
  id,
  children,
  sel,
  onSelect,
  marker,
}: {
  id: SecId;
  children: React.ReactNode;
  sel: SecId | null;
  onSelect: (id: SecId) => void;
  /** The margin anchor — options B and C hang the evidence affordance here. */
  marker?: React.ReactNode;
}) {
  const active = sel === id;
  return (
    <div
      onClick={() => onSelect(id)}
      style={{
        position: "relative",
        // The anchor lives INSIDE the section, never in the margin outside it:
        // the sheet sits in an overflow-y:auto column, and a browser clips the
        // cross axis of a scroll container — so a marker at right:-46 renders
        // perfectly in isolation and is invisible on the board.
        padding: marker ? "17px 58px 17px 18px" : "17px 18px",
        borderRadius: R,
        cursor: "pointer",
        background: active ? T.accentBg : "transparent",
        boxShadow: active ? `inset 3px 0 0 ${T.accent}` : "none",
        transition: "background .12s",
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <Label>{SEC_TITLE[id]}</Label>
      </div>
      {children}
      {marker && <div style={{ position: "absolute", top: 12, right: 14 }}>{marker}</div>}
    </div>
  );
}

function Sheet({
  d,
  m,
  win,
  mode,
  sel,
  onSelect,
  marker,
  onNumber,
  changed,
  lineM = {},
}: {
  d: Draft;
  m: number;
  win: Win;
  mode: "ballpark" | "detailed";
  sel: SecId | null;
  onSelect: (id: SecId) => void;
  marker?: (id: SecId) => React.ReactNode;
  /** A number is its own affordance — clicking one opens its trace. */
  onNumber?: (id: SecId) => void;
  /** The section the last chat turn moved, and what it did. */
  changed?: { sec: SecId; note: string } | null;
  /** Read only to price the lines. The sheet never displays a margin. */
  lineM?: Record<string, number>;
}) {
  const ids = mode === "ballpark" ? CLIENT_BALLPARK : CLIENT_DETAILED;
  const p = sheetPrice(d, m, lineM);
  // ⭐ THE SHEET NO LONGER COMPUTES bandPrice, fits OR marginToFit, and the
  //    linter is what told me: three dead locals the moment the cost-derived
  //    numbers came off the document. A component that has stopped needing a
  //    value is the cleanest possible proof that the value is gone from the
  //    screen — better evidence than reading the JSX.

  // Only the client-facing sections. `comps` and `sourcing` are rail-only now,
  // so the sheet's map is deliberately PARTIAL — the type says so, which means
  // adding a section back to the sheet has to be a decision rather than a slip.
  const body: Partial<Record<SecId, React.ReactNode>> = {
    brief: (
      <div style={{ fontSize: 14.5, lineHeight: 1.62, color: T.ink }}>
        {/* The classification and its margin band used to sit here. They are a
            fact about how WE price, not about the job, so they moved to the
            rail with the rest of the analysis. */}
        <strong>{BRIEF.client}</strong> · {BRIEF.job}. Reset window opens {win.date}, {win.days} days out. Finish not
        specified. Budget of {money(BRIEF.statedBudget)} stated on the call.
      </div>
    ),
    build: (
      <div>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.ink, marginBottom: 4 }}>{d.name}</div>
        <div style={{ fontSize: 13.5, color: T.ink2, marginBottom: 8 }}>{d.method}</div>
        <div style={{ fontSize: 14.5, lineHeight: 1.62, color: T.ink }}>{d.why}</div>
        {d.flag && (
          <div
            style={{
              marginTop: 10,
              background: T.warnBg,
              border: `1px solid ${T.warn}33`,
              borderRadius: R,
              padding: "10px 12px",
              fontSize: 12.5,
              lineHeight: 1.55,
              color: T.ink,
            }}
          >
            <Chip tone="amber">tariff exposure</Chip>{" "}
            <span style={{ display: "block", marginTop: 6 }}>{d.flag}</span>
          </div>
        )}
      </div>
    ),
    number: (
      <div>
        {/* ⛔ ONE NUMBER. THIS SHEET IS WHAT GOES TO THE CLIENT (Erez, 18 Aug).
            "The centre page is the estimate that is basically ready to send to
             the client. It should NOT have our cost, perceived value and the
             target price — not in the ballpark, not in the detail."

            He is right, and I had put all three here that same morning on his
            "add the three pricing views" instruction, which I read as add them
            to the SHEET. Their §1 does require three views per estimate — but a
            VIEW is something the estimator reads. Cost, the band midpoint and
            the budget we are reverse-engineering against are three facts about
            US, and a document that gets forwarded carries none of them.

            All three now sit together in the left rail with the rest of the
            analysis. Same rule as before, applied properly this time: the
            estimate carries PRICE; everything that explains the price lives
            beside it, not in it. */}
        <div
          onClick={(e) => {
            if (onNumber) {
              e.stopPropagation();
              onNumber("number");
            }
          }}
          style={{
            display: "inline-block",
            padding: onNumber ? "6px 10px" : 0,
            margin: onNumber ? "-6px -10px" : 0,
            borderRadius: R_CTL,
            background: onNumber ? T.accentBg : "transparent",
            cursor: onNumber ? "pointer" : "default",
          }}
        >
          <Label>price</Label>
          <div style={{ marginTop: 3 }}>
            <Money v={p} size={36} color={T.ink} />
          </div>
          <div style={{ fontSize: 13.5, color: T.ink2, marginTop: 5 }}>
            {money(Math.round(p / d.units))} a unit · {d.units} units · held for 30 days from the date above
          </div>
        </div>
      </div>
    ),
    bom: (
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <tbody>
          {d.bom.map((b) => (
            <tr key={b.label}>
              <td style={{ padding: "6px 8px", color: T.ink, borderBottom: `1px solid ${T.line}` }}>
                {/* No "stale rate" chip and no rate age. How fresh OUR reference
                    data is explains the number to us; it does not belong in a
                    document a client forwards to their procurement team. Both
                    are in the rail, on the same line. */}
                {b.label}
              </td>
              <td
                style={{
                  padding: "6px 8px",
                  textAlign: "right",
                  fontFamily: MONO,
                  color: T.ink,
                  whiteSpace: "nowrap",
                  borderBottom: `1px solid ${T.line}`,
                  verticalAlign: "top",
                }}
              >
                {money(linePrice(b, m, lineM))}
              </td>
            </tr>
          ))}
          <tr>
            <td style={{ padding: "8px", fontWeight: 600, color: T.ink }}>Price</td>
            <td style={{ padding: "8px", textAlign: "right", fontFamily: MONO, fontWeight: 600, color: T.ink }}>
              {money(p)}
            </td>
          </tr>
        </tbody>
      </table>
    ),
    lead: (
      <div style={{ display: "flex", gap: 26, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <Label>lead time</Label>
          <div style={{ fontFamily: MONO, fontSize: 21, fontWeight: 600, color: T.ink, marginTop: 2 }}>
            {d.leadDays} days
          </div>
        </div>
        <div>
          <Label>slack to the window</Label>
          <div
            style={{
              fontFamily: MONO,
              fontSize: 21,
              fontWeight: 600,
              marginTop: 2,
              color: win.days - d.leadDays > 20 ? T.good : T.warn,
            }}
          >
            {win.days - d.leadDays} days
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Label>route</Label>
          <div style={{ fontSize: 13, color: T.ink, marginTop: 3 }}>{d.route}</div>
        </div>
      </div>
    ),
    validity: (
      <div style={{ fontSize: 14.5, lineHeight: 1.62, color: T.ink }}>
        Valid 30 days from issue. Steel is the volatile input: a 5% move in the hot-rolled index moves this draft by
        roughly 2.1% of the price, and nothing else on this estimate moves it by more than 0.6%. After 30 days the sheet is
        recomputed rather than re-signed.
      </div>
    ),
    refusal: (
      <div
        style={{
          background: T.stopBg,
          border: `1px solid ${T.stop}33`,
          borderRadius: R,
          padding: "12px 14px",
        }}
      >
        {/* ⛔ THIS SECTION USED TO EXPLAIN OUR SCORING MODEL'S DATA GAPS —
            §3.7.1 weights, the §6 job record, how old our reference rates are.
            Every word of that is a fact about our system, and a client reading
            it learns nothing except that we are unsure. What a client SHOULD see
            here is what is not priced and what would price it, in their terms.
            The conversion-score refusal is now in the rail, where it is the
            sharpest thing on the screen for an estimator. */}
        <div style={{ fontSize: 14.5, lineHeight: 1.62, color: T.ink }}>
          <strong>Two items are not priced in this estimate.</strong> The printed-film finish is quoted on an assumed
          specification — a finish sample or a spec sheet fixes it. The custom hinge is excluded entirely until its
          drawing is final, and the total above does not include it.
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.55, color: T.ink2, marginTop: 8 }}>
          Section 232 duty is calculated from the published tariff schedule rather than from a filed entry, so the
          landed figure is an estimate until the first shipment clears.
        </div>
      </div>
    ),
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {ids.map((id, i) => (
        <div key={id}>
          <Section id={id} sel={sel} onSelect={onSelect} marker={marker?.(id)}>
            {changed?.sec === id && (
              <div style={{ marginBottom: 8 }}>
                <Chip tone="green">just updated · {changed.note}</Chip>
              </div>
            )}
            {body[id]}
          </Section>
          {i < ids.length - 1 && <div style={{ height: 1, background: T.line, margin: "2px 16px" }} />}
        </div>
      ))}
    </div>
  );
}

// ═══ THE CHAT ════════════════════════════════════════════════════════════════
// Erez, 15 Aug 2026: "On the right have the actual chat interface, because the
// interaction is with the chat also. The user talks to the chat, and the chat
// updates the PDF and the boxes information."
//
// This is the correction that mattered most in his review, and it is a concept
// correction rather than a layout one: the concept page says the conversation IS
// the product and the screens are a mirror. Three columns without a chat had the
// mirror and no product.
//
// So the conversation genuinely drives the other two columns. Every suggested
// turn below MUTATES the sheet — the active draft, the mode, the margin dial,
// even the reset window — and marks the section it moved. Nothing is a canned
// screenshot of a chat.

type Turn = {
  id: string;
  /** What the salesperson says. */
  user: string;
  /** What the agent did, as tool lines — the same shape omnigent renders. */
  tools: string[];
  /** What the agent says back. */
  reply: string;
  /** The section it moved, and the one-line note that appears on it. */
  changed: { sec: SecId; note: string };
};

const TURNS: Turn[] = [
  {
    id: "fit",
    user: "Make it fit the $650,000 they floated.",
    tools: ["solve_for_budget · 3 drafts", "pv_bands · §5", "estimate_lines"],
    reply:
      "D1 does not fit at any margin inside the band — it would need 32.0%, which is thirteen points below the floor. D2 lands at $596,830 at 47%, $53,170 under. I have switched the sheet to D2; the target price box now reads against it. I have not touched the margin, because changing the build was the cheaper lever.",
    changed: { sec: "number", note: "switched to D2 to fit $650,000" },
  },
  {
    id: "powder",
    user: "Why is powder coat so expensive?",
    tools: ["vendors · 1 qualified", "rfqs · blind round", "vendor_scorecard · 0 rows"],
    reply:
      "One qualified vendor, and their standing quote is 22.8% above what the market returned on a blind RFQ for the same specification. I have opened the detailed sheet so the line is visible. What I cannot tell you is whether the premium buys reliability: no vendor on this job has any delivery history recorded.",
    changed: { sec: "sourcing", note: "opened the detailed sheet" },
  },
  {
    id: "margin",
    user: "What does 45% do to the price?",
    tools: ["estimate_lines", "pv_bands · §5"],
    reply:
      "45% is the floor of the published band for this class of work. On D2 that is $575,127 instead of $596,830 — you give up $21,703 and gain nothing you have asked for. The dial on the number box is moved; it is a what-if and nothing is saved.",
    changed: { sec: "number", note: "margin dial moved to 45%" },
  },
  {
    id: "window",
    user: "They moved the reset to November. Redo the lead time.",
    tools: ["routes · 5 re-priced", "jobs · historical transit", "reference_rates · freight"],
    reply:
      "That widens the window from 87 days to 118, so all three builds now land inside it with slack. It also removes the only argument D1 had — speed is no longer scarce, and it is still 40% more per unit. Do you want the drafts re-ranked on cost?",
    changed: { sec: "lead", note: "recomputed against a November window" },
  },
];

type Msg = { who: "user" | "agent"; text: string; tools?: string[]; attach?: string };

const OPENING: Msg[] = [
  {
    who: "user",
    text: "Ulta want the fragrance endcap refreshed across 240 stores. Reset window opens 6 October, sketch attached. No finish specified. They mentioned something about $650,000.",
    attach: "endcap-concept.pdf",
  },
  {
    who: "agent",
    text: "Two things before I price it. Is the $650,000 approved, or a number they floated? And is the printed-film finish on the table, or does this have to match the existing powder-coat programme?",
    tools: ["sugar_crm.accounts · Ulta", "jobs · 4 prior for this client"],
  },
  { who: "user", text: "Floated. And film is on the table, they asked about it." },
  {
    who: "agent",
    text: "Three builds worth putting in front of them. D1 is the only one with real slack on the window and it is 40% more per unit. D2 is what you have actually run for Ulta, twice, so its cost is a repeat rather than an estimate. D3 is the cheapest and the least evidenced — no printed-film job exists for this client, and it carries §232 exposure the comparables do not. All three are on the sheet.",
    tools: [
      "jobs · 1,483 decided → 31 matches",
      "job_assets · drawings re-ranked",
      "reference_rates · 7 tables of §10.1",
      "routes · 5 priced, deadline filter",
      "pv_bands · §5 → Structured / RFP",
    ],
  },
];

// ═══ THE SUMMARY ═════════════════════════════════════════════════════════════
// Erez, 15 August: a third view beside Ballpark and Detailed — "a timeline of
// everything that happened, all the decisions, all the conversation with the
// agent … and when there is actually a win/loss event, the summary will get
// updated and will represent the important information."
//
// It is kept by a named skill — PROPOSAL LEARNING AND SUMMARY — and that is one
// skill rather than two because it is one record: how an estimate came to say what
// it says, and how the job it became turned out, are the same thread. Split them
// and the reasoning sits in one place with the verdict on it in another.
//
// I had this wrong for a morning and it is worth keeping the correction visible.
// I shipped it as "derived, never authored — nothing writes it", importing the
// law the old analysis stage ran on. In an architecture where everything is
// agentic, a record nobody keeps is a record made by code. The agent keeps it.
//
// Two properties survive that correction and they are the ones that matter:
//
//   1. NOBODY TYPES IT. There is no form, no author, no write-up step at the end.
//      It accrues as the work happens, which is why it exists from the first
//      ballpark instead of arriving after the outcome.
//   2. EVERY ENTRY SAYS WHO ASSERTED IT. An entry the agent produced and an entry
//      a human confirmed must never look the same, or the view reads as a record
//      of the agent's work rather than the team's.
//
// In this mock the timeline is built from what actually happened in the mock:
// the four scripted turns and every send-back append to it as they are clicked.
// A demo that fills up as you use it cannot lie about growing.

type LogEntry = {
  at: string;
  actor: "human" | "agent";
  title: string;
  body: string;
  tools?: string[];
  sec?: SecId;
  /** What the write gate stamped. The agent cannot confirm its own work. */
  state: "asserted" | "confirmed";
};

/** The entries that produced the ballpark. Always present — an estimate that
 *  exists at all has been through every one of them. */
const ORIGIN: LogEntry[] = [
  {
    at: "09:12",
    actor: "human",
    title: "Brief received",
    body:
      "Ulta Beauty, fragrance endcap refresh across 240 stores, reset window opening 6 October. Sketch attached. No finish specified. $650,000 mentioned on the call.",
    state: "confirmed",
  },
  {
    at: "09:12",
    actor: "agent",
    title: "Two questions asked before pricing anything",
    body:
      "Is the $650,000 approved or floated? Is printed film on the table, or must this match the existing powder-coat programme? Both were answered before a number existed — the budget is recorded as stated, not agreed.",
    tools: ["sugar_crm.accounts · Ulta", "jobs · 4 prior for this client"],
    sec: "brief",
    state: "asserted",
  },
  {
    at: "09:13",
    actor: "agent",
    title: "History searched",
    body:
      "1,483 decided jobs reduced to 31 candidate matches on type, client, unit count and budget proximity; drawings re-ranked the shortlist. Seven §10.1 reference tables read at their effective dates, not at today's.",
    tools: ["jobs · 1,483 decided → 31 matches", "job_assets · drawings re-ranked", "reference_rates · 7 tables"],
    sec: "comps",
    state: "asserted",
  },
  {
    at: "09:13",
    actor: "agent",
    title: "Perceived-value band assigned · Structured / RFP",
    body:
      "A scoped request with a deadline attached is the band definition in their own §5. Target margin 45–50%. The band and its reasoning sit on the face of the estimate, so this is a decision anyone can argue with.",
    tools: ["pv_bands · §5 → Structured / RFP"],
    sec: "brief",
    state: "asserted",
  },
  {
    at: "09:14",
    actor: "agent",
    title: "Three drafts produced",
    body:
      "D1 Dallas, the only build with real slack on the window and 40% more per unit. D2 Vietnam, the build Outform have actually run for this client twice, so its cost is a repeat rather than an estimate. D3 aluminium and film, cheapest and least evidenced. Five manufacturing routes priced, deadline filter applied.",
    tools: ["routes · 5 priced, deadline filter", "estimate_lines · 3 drafts"],
    sec: "build",
    state: "asserted",
  },
  {
    at: "09:14",
    actor: "agent",
    title: "Refusal recorded",
    body:
      "No vendor on this job carries any delivery history, and no printed-film job exists for this client. Both are on the estimate as things it could not price, unprompted.",
    sec: "refusal",
    state: "asserted",
  },
];

function SummaryTimeline({ entries }: { entries: LogEntry[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {entries.map((e, i) => {
        const agent = e.actor === "agent";
        const last = i === entries.length - 1;
        return (
          <div key={`${e.at}-${e.title}-${i}`} style={{ display: "flex", gap: 12 }}>
            {/* The rail. A timeline that draws no line is a list. */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 26, flexShrink: 0 }}>
              <div
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: 6,
                  marginTop: 6,
                  background: agent ? T.accent : T.ink,
                  border: `2px solid ${T.paper}`,
                  boxShadow: `0 0 0 1px ${agent ? T.accent : T.ink}`,
                }}
              />
              {!last && <div style={{ flex: 1, width: 1, background: T.line, marginTop: 3 }} />}
            </div>

            <div style={{ flex: 1, minWidth: 0, paddingBottom: last ? 0 : 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
                <span style={{ fontFamily: MONO, fontSize: 11, color: T.ink3 }}>{e.at}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{e.title}</span>
                {/* The provenance stamp. This is the whole reason the view is
                    trustworthy: the agent cannot mark its own work confirmed. */}
                <Chip tone={e.state === "confirmed" ? "green" : "blue"}>
                  {e.state === "confirmed" ? "human confirmed" : "agent asserted"}
                </Chip>
                {e.sec && <Chip>{SEC_TITLE[e.sec]}</Chip>}
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.6, color: T.ink2 }}>{e.body}</div>
              {e.tools && (
                <div
                  style={{
                    marginTop: 7,
                    background: N.bgGray,
                    border: `1px solid ${T.line}`,
                    borderRadius: R,
                    padding: "6px 9px",
                  }}
                >
                  {e.tools.map((t) => (
                    <ToolLine key={t} t={t} />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** The last entry this estimate does not have yet — and the three fields that
 *  make it worth anything. Their §3.7.1 conversion model weights outcome at 40%
 *  and price position at 30%; their §6 job record stores neither. */
function OutcomePanel({ onAsk }: { onAsk: () => void }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>How this ended</div>
        <Chip tone="amber">not yet recorded</Chip>
      </div>

      <div style={{ background: T.paper, border: `1px solid ${T.line}`, borderRadius: R, padding: "13px 14px" }}>
        <div style={{ fontSize: 12.5, lineHeight: 1.6, color: T.ink2 }}>
          When this estimate is won or lost, that becomes the last entry on the summary and everything above it
          gets read in the light of the result.
        </div>

        <div style={{ marginTop: 12 }}>
          <Label>the three fields that decide whether any of it is learnable</Label>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            <Row k="Price quoted" v="—" />
            <Row k="Price settled" v="—" />
            <Row k="Did the client push back?" v="—" tone="amber" />
          </div>
        </div>

        <div style={{ marginTop: 12, fontSize: 11.5, lineHeight: 1.6, color: T.ink3 }}>
          A win tells you a price was acceptable, never that it was optimal. Without the gap between quoted and
          settled, a model trained on wins alone walks the margin down for ever — the tool built to protect margin
          teaching them to leave more on the table.
        </div>

        <button
          onClick={onAsk}
          style={{
            marginTop: 13,
            width: "100%",
            fontFamily: F,
            fontSize: 12,
            fontWeight: 500,
            padding: "8px 12px",
            borderRadius: R_CTL,
            border: `1px solid ${T.line}`,
            background: N.bgGray,
            color: T.ink,
            cursor: "pointer",
          }}
        >
          Record the outcome
        </button>
        <div style={{ marginTop: 6, fontFamily: MONO, fontSize: 10.5, color: T.ink3, textAlign: "center" }}>
          asks the agent · nothing on this page edits
        </div>
      </div>
    </>
  );
}

function ToolLine({ t }: { t: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontFamily: MONO,
        fontSize: 10.5,
        color: T.ink3,
        padding: "2px 0",
      }}
    >
      <span style={{ color: T.good }}>✓</span>
      <span>{t}</span>
    </div>
  );
}

function Bubble({ m }: { m: Msg }) {
  const user = m.who === "user";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: user ? "flex-end" : "flex-start", gap: 5 }}>
      {!user && m.tools && (
        <div
          style={{
            width: "100%",
            background: N.bgGray,
            border: `1px solid ${T.line}`,
            borderRadius: R,
            padding: "7px 10px",
          }}
        >
          {m.tools.map((t) => (
            <ToolLine key={t} t={t} />
          ))}
        </div>
      )}
      <div
        style={{
          maxWidth: "92%",
          background: user ? T.accentBg : T.paper,
          border: `1px solid ${user ? `${T.accent}33` : T.line}`,
          borderRadius: R,
          padding: "9px 12px",
          fontSize: 12.5,
          lineHeight: 1.6,
          color: T.ink,
        }}
      >
        {m.text}
        {m.attach && (
          <div style={{ marginTop: 7 }}>
            <Chip tone="gray">📎 {m.attach}</Chip>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══ THE WORKBENCH ═══════════════════════════════════════════════════════════
// One screen, three columns, in Erez's order: "take option B, take the box
// panel, move it to the left. Then the PDF in the centre, then on the right the
// actual chat interface."
//
// WHY EACH COLUMN IS THE WIDTH IT IS, since he was specific about size:
//   left  660 — option B's drawer reading width. He said B's box "is a lot nicer
//                than the box of option A, it feels better", and the reason is
//                measurable rather than aesthetic: A's rail cards were ~940 wide
//                with 62px labels, so the reasoning ran 14 words to a line. B's
//                narrower column reads at 9–11, which is a comfortable measure.
//   centre 900 — the PDF width from options A and B, which he approved twice.
//   right 580 — enough for a real transcript without the chat competing with the
//                document for attention. The conversation drives; it does not
//                dominate.
//
// The left column keeps EVERY section, not only the selected one: the selected
// box is open in full, the rest are one-line entries that expand on click. That
// is option A's completeness inside option B's box design, and it is the one
// thing here that was not in his instruction — flagged rather than smuggled.

const SCREEN_W = 2360;
const SCREEN_H = 1440;
const COL_L = 660;
const COL_R = 580;

export function OutformWorkbench() {
  // Opens on D1 deliberately. D1 at 47% is $834,113 against a stated $650,000,
  // so the target-price box opens in its "does not fit" state and the first
  // chat turn genuinely resolves it. Opening on D2 made the agent say "I have
  // switched the sheet to D2" about a sheet that was already D2 — a demo that
  // narrates a change it did not make is worse than one that does nothing.
  const [draftIdx, setDraftIdx] = useState(0);
  const [mode, setMode] = useState<"ballpark" | "detailed" | "summary">("ballpark");
  const [sel, setSel] = useState<SecId>("number");
  const [m, setM] = useState(47);
  const [win, setWin] = useState<Win>(WIN_OCT);
  const [msgs, setMsgs] = useState<Msg[]>(OPENING);
  const [done, setDone] = useState<string[]>([]);
  const [changed, setChanged] = useState<{ sec: SecId; note: string } | null>(null);
  const [trace, setTrace] = useState<SecId | null>(null);
  // The summary is DERIVED, so it needs the events rather than the transcript:
  // a send-back is a decision and it never appears in `done`. Appending here is
  // what makes the third tab grow while you use the other two.
  const [log, setLog] = useState<LogEntry[]>([]);
  const stamp = () => `09:${17 + log.length}`;

  // Per-line margins. Empty means every line carries the estimate's margin, and
  // then the sheet total is arithmetically identical to cost over one minus m —
  // which is what makes it safe to show a total built from lines.
  const [lineM, setLineM] = useState<Record<string, number>>({});
  const setLine = (label: string, pct: number) => setLineM((prev) => ({ ...prev, [label]: pct }));

  // A transcript that does not follow its own newest message answers into a part
  // of the screen the person is not looking at — the same failure as replying on
  // the wrong channel, in miniature. Scroll the container itself rather than
  // calling scrollIntoView on the bubble: this panel lives inside a React Flow
  // canvas, and scrollIntoView walks up and scrolls every scrollable ancestor,
  // which drags the whole board.
  const feed = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = feed.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs.length]);

  const d = DRAFTS[draftIdx];
  const ids = mode === "ballpark" ? CLIENT_BALLPARK : CLIENT_DETAILED;

  // Keep the selection legal: switching to ballpark must not leave the reasoning
  // column pointing at a section the sheet no longer shows.
  const safeSel: SecId = ids.includes(sel) ? sel : "number";

  const run = (t: Turn) => {
    setMsgs((prev) => [...prev, { who: "user", text: t.user }, { who: "agent", text: t.reply, tools: t.tools }]);
    setDone((prev) => [...prev, t.id]);
    if (t.id === "fit") setDraftIdx(1);
    if (t.id === "powder") setMode("detailed");
    if (t.id === "margin") setM(45);
    if (t.id === "window") setWin(WIN_NOV);
    setSel(t.changed.sec);
    setChanged(t.changed);
    setLog((prev) => [
      ...prev,
      {
        at: stamp(),
        actor: "agent",
        title: t.user,
        body: t.reply,
        tools: t.tools,
        sec: t.changed.sec,
        state: "asserted",
      },
    ]);
  };

  const sendBack = (id: SecId) => {
    setMsgs((prev) => [
      ...prev,
      { who: "user", text: `Rework "${SEC_TITLE[id]}" — this section needs another pass.` },
      {
        who: "agent",
        text: `Taking that section back. The rest of the estimate stands; I will only rewrite ${SEC_TITLE[
          id
        ].toLowerCase()} and tell you what changed.`,
        tools: [`estimates · reopen section ${id}`],
      },
    ]);
    setSel(id);
    setChanged({ sec: id, note: "sent back for another pass" });
    // A human rejecting a section is the strongest signal on the whole timeline,
    // and it is the one a transcript-shaped record loses.
    setLog((prev) => [
      ...prev,
      {
        at: stamp(),
        actor: "human",
        title: `"${SEC_TITLE[id]}" sent back`,
        body: "An estimator rejected this section and asked for another pass. The rest of the estimate stands.",
        sec: id,
        state: "confirmed",
      },
    ]);
  };

  /** The outcome is not a field on this page — it is a question for the agent.
   *  Every affordance asks; nothing here edits. */
  const askOutcome = () => {
    setMode("summary");
    setMsgs((prev) => [
      ...prev,
      { who: "user", text: "This one closed. Record the outcome." },
      {
        who: "agent",
        text:
          "Three things and I will close it out. What price went out in the end, what price was settled, and did they push back on price at all before they signed? The gap between the first two is the only elasticity signal this job will ever produce.",
        tools: ["price_decisions · 0 rows", "jobs · outcome fields empty"],
      },
    ]);
  };

  return (
    // nowheel/nodrag/nopan + stopPropagation is MANDATORY on an interactive board
    // node: React Flow's panOnDrag swallows the pointer before it reaches an
    // input, and everything looks right while nothing can be clicked.
    <div
      className="nowheel nodrag nopan"
      data-zoom
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: "relative",
        pointerEvents: "auto",
        zIndex: 2,
        width: SCREEN_W,
        height: SCREEN_H,
        background: T.desk,
        border: `1px solid ${T.lineHard}`,
        borderRadius: R,
        fontFamily: F,
        boxSizing: "border-box",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── APP CHROME ───────────────────────────────────────────────────── */}
      <div
        style={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "10px 16px",
          background: T.chrome,
          borderBottom: `1px solid ${T.line}`,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>Estimating</span>
        <span style={{ color: T.ink3 }}>/</span>
        <span style={{ fontSize: 13, color: T.ink2 }}>
          {BRIEF.client} — {BRIEF.job}
        </span>
        <div style={{ flex: 1 }} />
        <Chip tone="amber">demonstration data · DEMO- records, not quotable</Chip>
      </div>

      {/* ── TOOLBAR: the three drafts, and ballpark → detail ─────────────── */}
      <div
        style={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 16px",
          background: T.paper,
          borderBottom: `1px solid ${T.line}`,
        }}
      >
        <Label>drafts</Label>
        <div style={{ display: "flex", gap: 6 }}>
          {DRAFTS.map((x, i) => (
            <button
              key={x.id}
              onClick={() => setDraftIdx(i)}
              style={{
                fontFamily: F,
                fontSize: 12,
                fontWeight: 500,
                padding: "5px 10px",
                borderRadius: R_CTL,
                cursor: "pointer",
                border: `1px solid ${i === draftIdx ? T.ink : T.line}`,
                background: i === draftIdx ? T.ink : T.paper,
                color: i === draftIdx ? "#FFF" : T.ink2,
                whiteSpace: "nowrap",
              }}
            >
              {x.id} · {money(price(x.cost, m))}
            </button>
          ))}
        </div>

        <div style={{ width: 1, height: 22, background: T.line, margin: "0 6px" }} />

        <div style={{ display: "flex", border: `1px solid ${T.line}`, borderRadius: R_CTL, overflow: "hidden" }}>
          {/* Three one-word siblings. The set decides the shape of the label:
              "Summary" beside "Ballpark" and "Detailed", never a phrase. */}
          {(["ballpark", "detailed", "summary"] as const).map((x) => (
            <button
              key={x}
              onClick={() => setMode(x)}
              style={{
                fontFamily: F,
                fontSize: 12,
                fontWeight: 500,
                padding: "5px 12px",
                border: "none",
                cursor: "pointer",
                background: mode === x ? N.bgGray : T.paper,
                color: mode === x ? T.ink : T.ink3,
                textTransform: "capitalize",
              }}
            >
              {x}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: MONO, fontSize: 11, color: T.ink3 }}>
          {mode === "summary"
            ? `${ORIGIN.length + log.length} entries · kept as the work happens`
            : `${ids.length} sections · every one traceable`}
        </span>
        <button
          style={{
            fontFamily: F,
            fontSize: 12,
            fontWeight: 500,
            padding: "6px 12px",
            borderRadius: R_CTL,
            border: `1px solid ${T.line}`,
            background: T.paper,
            color: T.ink2,
            cursor: "pointer",
          }}
        >
          Download PDF
        </button>
      </div>

      {/* ── THREE COLUMNS ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", position: "relative" }}>
        {/* ═══ LEFT · THE REASONING ══════════════════════════════════════ */}
        <div
          className="nowheel"
          style={{
            flex: `0 0 ${COL_L}px`,
            width: COL_L,
            borderRight: `1px solid ${T.lineHard}`,
            background: T.chrome,
            overflowY: "auto",
            padding: 16,
          }}
        >
          {mode === "summary" ? (
            <OutcomePanel onAsk={askOutcome} />
          ) : (
            <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.ink }}>Why the estimate says this</div>
            <Chip tone="blue">{ids.length} sections</Chip>
          </div>

          <ReasonCard
            id={safeSel}
            d={d}
            m={m}
            win={win}
            setM={setM}
            active
            onSend={() => sendBack(safeSel)}
            lineM={lineM}
            setLine={setLine}
          />

          {/* ⛔ THE LIST OF EVERY OTHER SECTION IS GONE (Erez, 18 Aug).
              "I don't want to see the list of all the blocks, because the user
               can just click the actual document to change the blocks."

              The DOCUMENT is the navigation. The list was a second way to do one
              thing, and it cost the reasoning the room it needed.

              ── AND THE ROOM IS NOW SPENT ON THE ANALYSIS ─────────────────────
              "On the left panel is where we put all the analysis, all the
               considerations, all the variations that we can change."

              Everything below is rail-only, and each block is here for the same
              reason: it is a fact about US (what it cost, which band we applied,
              whose budget we are aiming at, how fresh our rates are) or a fact
              about SOMEBODY ELSE (another client's job, a vendor's quote).
              Neither survives being forwarded, which is the whole test for which
              side of the screen a thing belongs on. */}
          <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${T.lineHard}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: T.ink }}>Analysis</div>
              <Chip tone="red">never on the estimate</Chip>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ background: T.paper, border: `1px solid ${T.line}`, borderRadius: R, padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Three pricing views</div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 12, lineHeight: 1.5 }}>
                  Their §1 asks for three on every estimate. A view is something the ESTIMATOR reads.
                </div>
                <ThreeViews d={d} m={m} />
              </div>

              <div style={{ background: T.paper, border: `1px solid ${T.line}`, borderRadius: R, padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Confidence</div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 12, lineHeight: 1.5 }}>
                  §7 asks for a band with its contributing factors shown. §4 asks for a rationale on every
                  estimate. This is both, and it is what an estimator uses to decide how much review this needs.
                </div>
                <ConfidenceBand d={d} />
              </div>

              <div style={{ background: T.paper, border: `1px solid ${T.line}`, borderRadius: R, padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Comparable jobs</div>
                <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 12, lineHeight: 1.5 }}>
                  Other clients&rsquo; job numbers and unit prices. This can never be on a document that gets forwarded.
                </div>
                <CompsTable d={d} />
              </div>

              {/* The refusal that used to sit on the sheet. It is the sharpest
                  thing on this screen for an estimator and the least useful
                  thing on earth for a client, who would read it as "they are
                  not sure". */}
              <div style={{ background: T.stopBg, border: `1px solid ${T.stop}33`, borderRadius: R, padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 8 }}>
                  What this system refuses to compute
                </div>
                <div style={{ fontSize: 13.5, lineHeight: 1.6, color: T.ink }}>
                  <strong>No conversion probability score.</strong> Their §3.7.1 weights it 40% outcome, 30% price
                  position, 20% perceived value, 10% client behaviour — and their §6 job record stores none of the first
                  three. It does not record whether a job was won. The score is not estimated and not approximated.
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.55, color: T.ink2, marginTop: 8 }}>
                  Their §1 names Sugar CRM and their §6 already pulls Sugar Scores, and Sugar carries won and lost by
                  default — so the outcome data is most likely UNJOINED rather than absent. One join may turn this on.
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.55, color: T.ink2, marginTop: 8 }}>
                  Every comparable above is a DEMO- record. The agent will not attach a real outcome to fabricated data,
                  and says so before it is asked.
                </div>
              </div>

              {mode === "detailed" && (
                <div style={{ background: T.paper, border: `1px solid ${T.line}`, borderRadius: R, padding: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Sourcing</div>
                  <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 12, lineHeight: 1.5 }}>
                    What the Costing Template is still waiting on, and where each line&rsquo;s number came from.
                  </div>
                  <SourcingPanel d={d} />
                </div>
              )}
            </div>
          </div>
            </>
          )}
        </div>

        {/* ═══ CENTRE · THE PDF ══════════════════════════════════════════ */}
        <div
          className="nowheel"
          style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: 20, display: "flex", justifyContent: "center" }}
        >
          <div
            style={{
              width: 900,
              background: T.paper,
              border: `1px solid ${T.line}`,
              borderRadius: R,
              padding: "22px 10px",
              alignSelf: "flex-start",
            }}
          >
            <div style={{ padding: "0 16px 14px", borderBottom: `1px solid ${T.line}`, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                <div style={{ fontSize: 17, fontWeight: 600, color: T.ink, letterSpacing: "-.01em" }}>
                  {mode === "ballpark" ? "Ballpark estimate" : mode === "detailed" ? "Detailed estimate" : "Summary"}
                </div>
                <span style={{ fontFamily: MONO, fontSize: 11, color: T.ink3 }}>
                  {mode === "summary"
                    ? `${d.id} · opened 15 Aug 2026 · outcome not yet recorded`
                    : `${d.id} · issued 15 Aug 2026 · valid 30 days`}
                </span>
              </div>
            </div>
            {mode === "summary" ? (
              <div style={{ padding: "8px 16px 6px" }}>
                <div style={{ fontSize: 12.5, lineHeight: 1.6, color: T.ink3, marginBottom: 16 }}>
                  Everything that produced this estimate, in the order it happened — the decisions, what each one
                  moved, and the conversation that drove them. Kept by the Estimate Learning skill as
                  the work happens, so it exists from the first ballpark rather than being written up at the end.
                </div>
                <SummaryTimeline entries={[...ORIGIN, ...log]} />
              </div>
            ) : (
            <Sheet
              d={d}
              m={m}
              win={win}
              mode={mode}
              sel={safeSel}
              onSelect={setSel}
              onNumber={setTrace}
              changed={changed}
              lineM={lineM}
              marker={(id) => (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSel(id);
                  }}
                  title="why this section says what it says"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 15,
                    border: `1px solid ${safeSel === id ? T.accent : T.line}`,
                    background: safeSel === id ? T.accent : T.paper,
                    color: safeSel === id ? "#FFF" : T.accent,
                    fontFamily: MONO,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    lineHeight: 1,
                  }}
                >
                  ?
                </button>
              )}
            />
            )}
          </div>
        </div>

        {/* ═══ RIGHT · THE CHAT ══════════════════════════════════════════ */}
        <div
          style={{
            flex: `0 0 ${COL_R}px`,
            width: COL_R,
            borderLeft: `1px solid ${T.lineHard}`,
            background: T.chrome,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <div
            style={{
              flex: "0 0 auto",
              padding: "11px 14px",
              borderBottom: `1px solid ${T.line}`,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 4, background: T.good, display: "inline-block" }} />
            <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>Estimating agent</div>
            <div style={{ flex: 1 }} />
            <span style={{ fontFamily: MONO, fontSize: 10.5, color: T.ink3 }}>reading the estimating skill</span>
          </div>

          <div
            ref={feed}
            className="nowheel"
            style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 12 }}
          >
            {msgs.map((x, i) => (
              <Bubble key={i} m={x} />
            ))}
          </div>

          {/* The composer. Every chip is a real turn that moves the two columns
              to its left — that is the demonstration, not the styling. */}
          <div style={{ flex: "0 0 auto", borderTop: `1px solid ${T.line}`, padding: 12, background: T.paper }}>
            <Label>try one — each of these changes the estimate</Label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "8px 0 10px" }}>
              {TURNS.filter((t) => !done.includes(t.id)).map((t) => (
                <button
                  key={t.id}
                  onClick={() => run(t)}
                  style={{
                    textAlign: "left",
                    fontFamily: F,
                    fontSize: 12,
                    color: T.accent,
                    background: T.accentBg,
                    border: `1px solid ${T.accent}22`,
                    borderRadius: R_CTL,
                    padding: "7px 10px",
                    cursor: "pointer",
                  }}
                >
                  {t.user}
                </button>
              ))}
              {TURNS.every((t) => done.includes(t.id)) && (
                <div style={{ fontSize: 12, color: T.ink3, lineHeight: 1.55 }}>
                  That is every scripted turn. Each one moved the sheet or the reasoning beside it — switch a draft or
                  drag the margin dial to keep going.
                </div>
              )}
            </div>
            <div
              style={{
                border: `1px solid ${T.line}`,
                borderRadius: R_CTL,
                padding: "9px 11px",
                fontSize: 12.5,
                color: T.ink3,
                background: N.bgGray,
              }}
            >
              Ask the agent anything about this job…
            </div>
          </div>
        </div>

        {/* ── THE TRACE — a number is its own affordance. Kept from option C,
            which was never really a third layout: it is a behaviour that A and
            B can both carry, and it belongs on the number rather than in a
            column of its own. */}
        {trace && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(27,33,30,0.34)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 30,
            }}
            onClick={() => setTrace(null)}
          >
            <div
              className="nowheel"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 1040,
                maxHeight: "100%",
                overflowY: "auto",
                background: T.paper,
                border: `1px solid ${T.lineHard}`,
                borderRadius: R,
                boxShadow: "0 30px 70px rgba(27,33,30,0.28)",
                padding: 20,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div>
                  <Label>tracing</Label>
                  <div style={{ fontSize: 18, fontWeight: 600, color: T.ink, marginTop: 3 }}>
                    {money(sheetPrice(d, m, lineM))} — {SEC_TITLE[trace]}
                  </div>
                </div>
                <button
                  onClick={() => setTrace(null)}
                  style={{
                    border: `1px solid ${T.line}`,
                    background: T.paper,
                    borderRadius: R_CTL,
                    fontFamily: MONO,
                    fontSize: 12,
                    color: T.ink2,
                    padding: "5px 10px",
                    cursor: "pointer",
                  }}
                >
                  close
                </button>
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                  <ReasonCard id={trace} d={d} m={m} win={win} setM={setM} active />
                </div>
                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                  <div style={{ background: N.bgGray, border: `1px solid ${T.line}`, borderRadius: R, padding: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 8 }}>
                      What moves this number
                    </div>
                    {DRAFTS.map((x) => (
                      <div
                        key={x.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "6px 0",
                          borderBottom: `1px solid ${T.line}`,
                          fontSize: 12.5,
                          color: x.id === d.id ? T.ink : T.ink2,
                          fontWeight: x.id === d.id ? 600 : 400,
                        }}
                      >
                        <span>
                          {x.id} · {x.name}
                        </span>
                        <span style={{ fontFamily: MONO, whiteSpace: "nowrap", marginLeft: 12 }}>
                          {money(price(x.cost, m))}
                        </span>
                      </div>
                    ))}
                    <div style={{ fontSize: 12, lineHeight: 1.6, color: T.ink3, marginTop: 10 }}>
                      The spread between drafts is {money(price(DRAFTS[0].cost, m) - price(DRAFTS[2].cost, m))} — larger
                      than anything the margin dial can do inside the published band. Changing the build is the lever;
                      changing the price is the last resort.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══ THE SECTION ═════════════════════════════════════════════════════════════

const HEAD: React.CSSProperties = {
  font: "600 15px Inter, system-ui, sans-serif",
  letterSpacing: "-0.01em",
  color: "#2F3E4F",
  marginBottom: 4,
};

const SUB: React.CSSProperties = {
  font: "400 13px Inter, system-ui, sans-serif",
  color: "#5B6B7C",
  marginBottom: 14,
  maxWidth: 1600,
  lineHeight: 1.55,
};

const NOTE: React.CSSProperties = {
  font: "400 13px Inter, system-ui, sans-serif",
  color: "#5B6B7C",
  maxWidth: 1180,
  marginTop: 22,
  lineHeight: 1.65,
};

export function OutformProductUX() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 46 }}>
      <div>
        <div style={HEAD}>0 · The user experience — a brief in, an estimate out</div>
        <Canvas d={JOURNEY} mode="contrast" />
      </div>

      <div>
        <div style={HEAD}>The main screen — reasoning, the document, and the conversation</div>
        <div style={SUB}>
          Three columns, in Erez&rsquo;s order: the reasoning box on the left, the estimate in the centre, the agent on
          the right. It is live. Send one of the suggested turns in the chat and watch the other two columns move — the
          active draft, the view, the margin dial and even the reset window are all changed by the conversation, and
          the section that moved is marked. Click any &ldquo;?&rdquo; to change what the left column explains, drag
          the margin bar inside &ldquo;The number&rdquo;, or click the big price to open its trace. Nothing here
          writes: the send-back button composes a message to the agent, and the dial is a what-if that stores nothing.
        </div>
        <div style={SUB}>
          <strong>THE CENTRE IS WHAT YOU SEND. THE LEFT IS EVERYTHING THAT EXPLAINS IT.</strong> Erez drew that line
          on 18 August and it now decides every element on the screen: the sheet carries only what a client may read,
          and anything that is a fact about US — our cost, the band we applied, whose budget we are aiming at, how old
          our rates are — or a fact about SOMEBODY ELSE — another client&rsquo;s job, a vendor&rsquo;s quote — is in
          the rail. That is why comparable jobs and sourcing are not sections of the document at all: <em>a comparable
          is another client&rsquo;s job number and a unit price.</em> The test is simply whether it survives being
          forwarded.
          <br />
          <br />
          Their §1 asks for <strong>three pricing views on every estimate</strong> — Our Cost, Perceived Value Price,
          Target Price Scenario. All three are in the rail, together, because a <em>view</em> is something the estimator
          reads. Open &ldquo;Price by component&rdquo; on the detailed sheet: the document shows what each line sells
          for, and the box beside it shows what that line cost, the margin it carries, and what it therefore sells for,
          with a control on every line. That is their §3.2 made usable — visibly keen on the parts a client can price
          for themselves, held on the parts they cannot. Move one line and the estimate&rsquo;s total moves with it.
          Nothing is saved until you tell the agent.
        </div>
        <div style={SUB}>
          <strong>Three views, one page — Ballpark, Detailed, Summary.</strong> The third is the record of how the
          estimate came to say what it says: every decision, what each one moved, and the conversation that drove
          them, with each entry stamped <em>agent asserted</em> or <em>human confirmed</em>. It is kept by the
          <strong> Estimate Learning</strong> skill as the work happens — nobody types it and there is
          no write-up step at the end — which is why it exists from the first ballpark. It genuinely grows here:
          run the chat turns or send a section back, then open Summary. Its last entry is the one this estimate
          does not have yet. When the job is won or lost, that outcome lands on the timeline, everything above it
          gets read in the light of the result, and the same skill proposes what the method should learn from it.
        </div>
        {/* ⛔ THE PROTOTYPE ITSELF IS NO LONGER RENDERED HERE (Erez, 18 Aug).
            "I want a section only with the product design, like a prototype app,
             and I want to put it next to the blueprint, what we took from each
             part."

            It has its own SectionBox on the board now, beside the blueprint
            analysis, so the thing we are building sits next to the document it
            came from. This section keeps the journey diagram and the written
            argument — the WHY — and the board carries the WHAT one box over.
            Rendering it in both places would be two copies of a live surface,
            which is the one thing worse than it being hard to find. */}
      </div>

      <div style={NOTE}>
        <strong>What his review changed, and why it was the right call.</strong> The three options all shared one fault
        and he named it first: there was no chat in any of them. That is not a layout miss, it is a concept miss &mdash;
        the concept page says the conversation IS the product and the screens are a mirror of it, so three columns of
        mirror with no product was the wrong picture of our own idea. The chat is now the thing that drives the other
        two columns rather than sitting beside them.
        <br />
        <br />
        <strong>Why option B&rsquo;s box won on feel, stated as measurement rather than taste.</strong> A&rsquo;s rail
        cards ran about 940px wide against 62px labels, which puts roughly fourteen words on a line; B&rsquo;s column
        reads at nine to eleven, which is inside the measure a person reads comfortably. Same content, same component
        &mdash; the width was doing the work.
        <br />
        <br />
        <strong>The list of sections is gone, and he was right about it.</strong> This note used to defend keeping
        every section in the left column, on the grounds that a panel blank for eight of nine would throw away option
        A&rsquo;s completeness. Erez: <em>&ldquo;the user can just click the actual document to change the blocks.&rdquo;</em>
        The document IS the navigation, so the list was a second way to do one thing &mdash; and the room it took is
        now spent on the analysis, which is what the column is for.
        <br />
        <br />
        <strong>What is deliberately absent.</strong> No editable field anywhere &mdash; a second, silent mutation path
        would put a hole in the §4 audit trail exactly the size of the UI. No conversion probability score on the
        estimate, because their own job record cannot feed it &mdash; the refusal, and the reason for it, is in the
        rail where an estimator can act on it rather than on a page a client reads as hesitation. No confirm-everything
        gate, because we have measured what that produces on two live boxes: Falcon 404 of 420 values never confirmed,
        Outform 40 of 40.
      </div>
    </div>
  );
}
